import { FC, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import moment from "moment";

import { isLeavingDateSections, submitFormOnEnter } from "./fieldEvents";

export const IO_FORMAT = "YYYY-MM-DD";
export const DISPLAY_FORMAT = "DD/MM/YYYY";

export const correctYear = (date: moment.Moment): moment.Moment => {
  const year = date.year();
  if (year > 0 && year < 100) {
    return date.clone().year(year + 2000);
  }
  return date;
};

export interface DatePickerProps {
  label: string;
  value: string | null;
  onChange: (value: string | null) => void;
  onBlur?: () => void;
  error?: string;
  disableFuture?: boolean;
  disabled?: boolean;
  clearable?: boolean;
}

export const DatePicker: FC<DatePickerProps> = ({
  label,
  value,
  onChange,
  onBlur,
  error,
  disableFuture = false,
  disabled = false,
  clearable = false,
}) => {
  const { t } = useTranslation();
  const [internalError, setInternalError] = useState<string>("");

  const inputRef = useRef<HTMLInputElement>(null);

  // Bumped when the 2-digit-year correction changes what was typed ("05" -> 2005), so the field
  // re-syncs to the corrected year even when the stored value itself does not change.
  const [resyncKey, setResyncKey] = useState(0);

  // Pickers 9 only keep typed sections that the value prop reflects. An incomplete or invalid entry
  // is reported upwards as null, so while the stored value is null the field gets its own invalid
  // value back; otherwise the year typed into "31/11/2023" would vanish.
  const [draft, setDraft] = useState<moment.Moment | null>(null);

  // The field's own date message is more specific than the form's (e.g. "required" while the typed
  // date is rejected and the stored value is therefore null), so it wins.
  const displayedError = internalError || error;
  // Memoised so the field only re-syncs its sections when the value (or resyncKey) changes.
  // resyncKey is a deliberate extra dependency: a new moment instance makes the field re-sync.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const dateValue = useMemo(() => (value ? moment(value, IO_FORMAT) : null), [value, resyncKey]);

  const toStoredValue = (date: moment.Moment): string => {
    const corrected = correctYear(date);
    if (corrected.year() !== date.year()) setResyncKey((key) => key + 1);
    return corrected.startOf("day").format(IO_FORMAT);
  };

  const handleChange = (momentDate: moment.Moment | null) => {
    setInternalError("");
    if (momentDate && momentDate.isValid()) {
      if (disableFuture && correctYear(momentDate).isAfter(moment(), "day")) {
        // The picker reports a typed future date as a valid change; never store it, or values derived
        // from it (e.g. Valid Until) are calculated before the blur rejects it.
        setDraft(momentDate);
        onChange(null);
        setInternalError(t("future_date_not_allowed"));
        return;
      }
      setDraft(null);
      onChange(toStoredValue(momentDate));
    } else {
      setDraft(momentDate);
      onChange(null);
    }
  };

  // Validates what was typed, read from the field's hidden input (the formatted sections, e.g.
  // "16/MM/YYYY" while incomplete), so the invalid/future-date messages stay the translated ones.
  const handleBlur = (event: React.FocusEvent<HTMLElement>) => {
    if (!isLeavingDateSections(event)) return;
    const inputValue = inputRef.current?.value ?? "";

    if (inputValue && inputValue.trim() !== "") {
      const parsedDate = moment(inputValue, DISPLAY_FORMAT, true);

      if (!parsedDate.isValid()) {
        onChange(null);
        setInternalError(t("invalid_date"));
        onBlur?.();
        return;
      }

      if (disableFuture && parsedDate.isAfter(moment(), "day")) {
        // The picker accepted this date, so there is no draft: keep the typed date visible.
        setDraft(parsedDate);
        onChange(null);
        setInternalError(t("future_date_not_allowed"));
        onBlur?.();
        return;
      }

      setInternalError("");
      onChange(toStoredValue(parsedDate));
    }

    onBlur?.();
  };

  return (
    <DesktopDatePicker
      format={DISPLAY_FORMAT}
      value={value === null && draft ? draft : dateValue}
      disableFuture={disableFuture}
      disabled={disabled}
      label={label}
      onChange={handleChange}
      inputRef={inputRef}
      slotProps={{
        textField: {
          helperText: displayedError || DISPLAY_FORMAT.toLowerCase(),
          fullWidth: true,
          size: "small",
          error: !!displayedError,
          onBlur: handleBlur,
          onKeyDown: submitFormOnEnter,
          sx: { minWidth: "250px" },
        },
        field: clearable
          ? {
              clearable: true,
              onClear: () => {
                setDraft(null);
                onChange(null);
              },
            }
          : undefined,
      }}
    />
  );
};

export default DatePicker;
